/**
 * Wikilink support for CodeMirror 6:
 *  - autocomplete inside `[[` against the titles endpoint
 *  - `[[...]]` decorated as links; Ctrl/Cmd+click (or plain click on the brackets) opens
 *  - frontmatter block dimmed in mono
 */
import { Decoration, EditorView, MatchDecorator, ViewPlugin, type DecorationSet, type ViewUpdate } from '@codemirror/view';
import { RangeSetBuilder } from '@codemirror/state';
import { autocompletion, type CompletionContext, type CompletionResult } from '@codemirror/autocomplete';
import { Api } from '../../lib/api';

const WIKILINK = /!?\[\[([^\[\]]+?)\]\]/g;

export function wikilinkCompletion() {
  return autocompletion({
    activateOnTyping: true,
    closeOnBlur: true,
    icons: false,
    override: [
      async (ctx: CompletionContext): Promise<CompletionResult | null> => {
        const before = ctx.matchBefore(/\[\[([^\[\]]*)$/);
        if (!before) return null;
        const query = before.text.slice(2);
        // Heading part typed: don't complete titles.
        if (query.includes('#') || query.includes('|')) return null;
        const hits = await Api.notes.titles(query, 25);
        if (ctx.aborted) return null;
        const after = ctx.state.doc.sliceString(ctx.pos, ctx.pos + 2) === ']]' ? '' : ']]';
        return {
          from: before.from + 2,
          filter: false,
          validFor: /^[^\[\]#|]*$/,
          options: hits.map((h) => ({
            label: h.alias ?? h.title,
            detail: `${h.type}  ${h.path}`,
            type: 'text',
            apply: (view, _c, from, to) => {
              const text = (h.alias ?? h.title) + after;
              view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length } });
            },
          })),
        };
      },
    ],
  });
}

const linkDecorator = new MatchDecorator({
  regexp: WIKILINK,
  decoration: (m) => Decoration.mark({ class: 'cm-wikilink', attributes: { 'data-target': m[1] } }),
});

export function wikilinkDecorations(onOpen: (target: string, opts: { split?: boolean }) => void) {
  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = linkDecorator.createDeco(view);
      }
      update(u: ViewUpdate) {
        this.decorations = linkDecorator.updateDeco(u, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
      eventHandlers: {
        mousedown(e: MouseEvent) {
          const el = (e.target as HTMLElement).closest('.cm-wikilink') as HTMLElement | null;
          if (!el) return false;
          const mod = e.ctrlKey || e.metaKey;
          // Plain click puts the cursor in the link (editing); Ctrl/Cmd+click follows it.
          if (!mod) return false;
          e.preventDefault();
          onOpen(el.dataset.target ?? '', { split: e.shiftKey });
          return true;
        },
      },
    },
  );
  return plugin;
}

/** Dim the YAML frontmatter block and render it in mono. */
export const frontmatterDecorations = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = this.build(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged) this.decorations = this.build(u.view);
    }
    build(view: EditorView): DecorationSet {
      const b = new RangeSetBuilder<Decoration>();
      const doc = view.state.doc;
      if (doc.lines < 2 || doc.line(1).text.trim() !== '---') return b.finish();
      let end = -1;
      for (let i = 2; i <= Math.min(doc.lines, 200); i++) {
        if (doc.line(i).text.trim() === '---') {
          end = i;
          break;
        }
      }
      if (end < 0) return b.finish();
      for (let i = 1; i <= end; i++) {
        const line = doc.line(i);
        b.add(line.from, line.from, Decoration.line({ class: i === 1 || i === end ? 'cm-frontmatter cm-frontmatter-fence' : 'cm-frontmatter' }));
      }
      return b.finish();
    }
  },
  { decorations: (v) => v.decorations },
);
